import type { SchedulerSummary } from '@/lib/api/generated/model';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { schedulerLine, type SchedulerLine } from '../schedulerLine';

const TONE_CLASS: Record<SchedulerLine['tone'], string> = {
  busy: 'text-foreground',
  warning: 'text-state-warning',
};

/**
 * What the scheduler is doing for a sub-workflow batch (spec 239 US1): choosing,
 * or stalled. Nothing for a batch without one, or one at rest.
 *
 * It never shows a decision's reason: those are the LLM's words, and they live in
 * the run's activity feed and on the parent's comments.
 */
export function SchedulerStatus({
  scheduler,
}: {
  scheduler: SchedulerSummary | null | undefined;
}) {
  const line = schedulerLine(scheduler);
  if (!line) return null;
  return <Line line={line} />;
}

function Line({ line }: { line: SchedulerLine }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col gap-0.5 text-xs ${TONE_CLASS[line.tone]}`}
    >
      <div className="flex items-center gap-1.5">
        {line.tone === 'busy' ? (
          <span
            aria-hidden="true"
            className="bg-primary size-1.5 shrink-0 rounded-full motion-safe:animate-pulse"
          />
        ) : null}
        <span className={line.tone === 'warning' ? 'font-medium' : undefined}>
          {line.title}
        </span>
        {line.decidedAt ? (
          <span
            className="text-muted-foreground"
            title={formatExactTime(line.decidedAt)}
          >
            {formatRelativeTime(line.decidedAt)}
          </span>
        ) : null}
      </div>
      {line.hint ? <p className="text-muted-foreground">{line.hint}</p> : null}
    </div>
  );
}
